/**
 * graph.ts — pure, zero-dependency ego-graph geometry + traversal (Story UI-U8).
 *
 * Ported from Sebastian's kunde-struktur-tab.tsx (deriveView / edgePoint /
 * buildAdjacency / pathToCenter / neighborsOf), adapted to the live
 * BeziehungsnetzDto: the backend already classifies each relation's kategorie
 * relative to the queried subject (the ego centre), so the layout positions
 * nodes by kategorie band (Mutter above, Schwestern mid-right, Töchter below,
 * übrige Beziehungen in the left column) — no client-side re-derivation, no
 * chart lib, no layout engine. NO React here → unit-testable.
 *
 * Re-centering to another firm re-fetches that firm's ego graph (each centre =
 * one server read), so `centerId` is normally `dto.subject_id`; it stays a param
 * for testability.
 *
 * [Source: brokly-design-reference/components/brokly/kunde-struktur-tab.tsx]
 */

import type {
  BeziehungsnetzDto,
  BeziehungsnetzEntityDto,
  RelKategorie,
} from '@brokly/shared';

export type NodeCategory = RelKategorie | 'diese';

export interface GraphNode extends BeziehungsnetzEntityDto {
  category: NodeCategory;
  x: number;
  y: number;
}

export interface GraphEdge {
  id: string;
  a: string;
  b: string;
  category: RelKategorie;
  label: string | null;
}

export interface DerivedView {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

// Canvas geometry (unchanged from the prototype).
export const CANVAS_W = 1040;
export const CANVAS_H = 640;
export const CX = 470;
export const CY = 300;
// Half card size incl. a small gap — edges end exactly at the card border.
export const NODE_HW = 84;
export const NODE_HH = 29;

/** Konzern kategorien (the "Nur Konzernstruktur" quick filter). */
export const KONZERN_CATEGORIES: RelKategorie[] = ['mutter', 'schwester', 'tochter'];

/** Kategorien in filter/legend order (Konzern first). */
export const FILTER_CATEGORIES: RelKategorie[] = [
  'mutter',
  'schwester',
  'tochter',
  'person',
  'cobroker',
  'partner',
  'buergschaft',
];

/**
 * Classify each entity relative to the centre: the centre wins ('diese'); every
 * other node takes the kategorie of the relation that connects it to the centre.
 * (The endpoint returns a one-hop ego graph, so each node has exactly one such
 * relation.) Nodes with no relation to the centre are dropped.
 */
function categoryByEntity(
  dto: BeziehungsnetzDto,
  centerId: string,
): Map<string, NodeCategory> {
  const cat = new Map<string, NodeCategory>();
  cat.set(centerId, 'diese');
  for (const r of dto.relations) {
    if (r.from === centerId && r.to !== centerId) cat.set(r.to, r.kategorie);
    else if (r.to === centerId && r.from !== centerId) cat.set(r.from, r.kategorie);
  }
  return cat;
}

/**
 * Build the positioned ego view: nodes placed by kategorie band + edges limited
 * to visible nodes. Deterministic — same input → same output (unit-tested).
 */
export function deriveView(dto: BeziehungsnetzDto, centerId: string): DerivedView {
  const byId = new Map(dto.entities.map((e) => [e.id, e]));
  const catOf = categoryByEntity(dto, centerId);

  const parents: string[] = [];
  const siblings: string[] = [];
  const children: string[] = [];
  const others: string[] = [];
  for (const [id, c] of catOf) {
    if (id === centerId) continue;
    // Skip ids that have no entity (a relation can reference a node the payload
    // didn't include) — they must never become a positioned, edge-visible node.
    if (!byId.has(id)) continue;
    if (c === 'mutter') parents.push(id);
    else if (c === 'schwester') siblings.push(id);
    else if (c === 'tochter') children.push(id);
    else others.push(id);
  }

  const pos = new Map<string, { x: number; y: number }>();
  pos.set(centerId, { x: CX, y: CY });

  siblings.forEach((id, i) => pos.set(id, { x: CX + 235 + i * 205, y: CY }));

  const clusterRight = siblings.length ? CX + 235 + (siblings.length - 1) * 205 : CX;
  const clusterMid = (CX + clusterRight) / 2;
  const parW = (parents.length - 1) * 220;
  parents.forEach((id, i) => pos.set(id, { x: clusterMid - parW / 2 + i * 220, y: 84 }));

  const chW = (children.length - 1) * 195;
  children.forEach((id, i) => pos.set(id, { x: CX - chW / 2 + i * 195, y: 536 }));

  const oTop = 104;
  const oBot = 496;
  others.forEach((id, i) => {
    const y = others.length > 1 ? oTop + i * ((oBot - oTop) / (others.length - 1)) : CY;
    pos.set(id, { x: 104, y });
  });

  const visible = new Set(pos.keys());
  const nodes: GraphNode[] = [...visible]
    .map((id) => {
      const e = byId.get(id);
      const p = pos.get(id)!;
      if (!e) return null;
      return { ...e, category: catOf.get(id) ?? 'partner', x: p.x, y: p.y };
    })
    .filter((n): n is GraphNode => n !== null);

  const edges: GraphEdge[] = [];
  for (const r of dto.relations) {
    if (!visible.has(r.from) || !visible.has(r.to)) continue;
    edges.push({ id: r.id, a: r.from, b: r.to, category: r.kategorie, label: r.label });
  }

  return { nodes, edges };
}

/** Intersection of the ray from→to with `from`'s card border (edge trimming). */
export function edgePoint(
  from: { x: number; y: number },
  to: { x: number; y: number },
): { x: number; y: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === 0) return { x: from.x, y: from.y };
  const tx = NODE_HW / (Math.abs(dx) || 1e-6);
  const ty = NODE_HH / (Math.abs(dy) || 1e-6);
  const t = Math.min(tx, ty);
  return { x: from.x + dx * t, y: from.y + dy * t };
}

type Adjacency = Map<string, { to: string; edgeId: string }[]>;

/** Undirected adjacency between visible nodes. */
export function buildAdjacency(nodes: GraphNode[], edges: GraphEdge[]): Adjacency {
  const visible = new Set(nodes.map((n) => n.id));
  const adj: Adjacency = new Map();
  for (const n of nodes) adj.set(n.id, []);
  for (const e of edges) {
    if (!visible.has(e.a) || !visible.has(e.b)) continue;
    adj.get(e.a)!.push({ to: e.b, edgeId: e.id });
    adj.get(e.b)!.push({ to: e.a, edgeId: e.id });
  }
  return adj;
}

/** BFS shortest path from `startId` back to the centre (nodes + edges on the path). */
export function pathToCenter(
  startId: string,
  centerId: string,
  adj: Adjacency,
): { nodes: Set<string>; edges: Set<string> } {
  const nodes = new Set<string>([startId]);
  const edges = new Set<string>();
  if (startId === centerId) return { nodes, edges };

  const prev = new Map<string, { from: string; edgeId: string }>();
  const visited = new Set<string>([startId]);
  const queue = [startId];
  while (queue.length) {
    const cur = queue.shift()!;
    if (cur === centerId) break;
    for (const { to, edgeId } of adj.get(cur) ?? []) {
      if (!visited.has(to)) {
        visited.add(to);
        prev.set(to, { from: cur, edgeId });
        queue.push(to);
      }
    }
  }
  if (!prev.has(centerId)) return { nodes, edges };

  let cur = centerId;
  nodes.add(cur);
  while (cur !== startId) {
    const p = prev.get(cur);
    if (!p) break;
    edges.add(p.edgeId);
    nodes.add(p.from);
    cur = p.from;
  }
  return { nodes, edges };
}

/** Direct neighbours of `id` (for the Liste cross-highlight). */
export function neighborsOf(id: string, adj: Adjacency): Set<string> {
  const set = new Set<string>([id]);
  for (const { to } of adj.get(id) ?? []) set.add(to);
  return set;
}
